import { FetchResult, gql, MutationFunctionOptions, MutationResult, useMutation } from '@apollo/client';

import { NexoyaMutation, NexoyaMutationUpdatePortfolioContentTargetBoundariesArgs } from '../../types/types';
import { NexoyaPortfolioContentTargetBoundariesInput } from '../../types';

import { useTeam } from '../../context/TeamProvider';

import { track } from '../../constants/datadog';
import { EVENT } from '../../constants/events';
import { PORTFOLIO_PARENT_CONTENTS_QUERY } from '../content/queryPortfolioContents';
import { buildPortfolioContentFilterVariables } from '../../routes/portfolio/utils/content';
import { useContentFilterStore } from '../../store/content-filter';

const UPDATE_PORTFOLIO_CONTENT_TARGET_BOUNDARIES = gql`
  mutation UpdatePortfolioContentTargetBoundaries(
    $teamId: Float!
    $portfolioId: Float!
    $contents: [Float!]
    $newTarget: PortfolioContentTargetBoundariesInput!
  ) {
    updatePortfolioContentTargetBoundaries(
      teamId: $teamId
      portfolioId: $portfolioId
      onlyContentIds: $contents
      newTarget: $newTarget
    )
  }
`;
type Options = {
  portfolioId: number;
  contents: number[];
  newTarget: NexoyaPortfolioContentTargetBoundariesInput;
};

type IMutationResult = [
  (
    options?: MutationFunctionOptions<
      Pick<NexoyaMutation, 'updatePortfolioContentTargetBoundaries'>,
      NexoyaMutationUpdatePortfolioContentTargetBoundariesArgs & { contents?: number[] }
    >,
  ) => Promise<FetchResult>,
  MutationResult,
];

function useUpdatePortfolioContentTargetBoundaries({ portfolioId, contents, newTarget }: Options): IMutationResult {
  const { teamId } = useTeam();
  const filterStore = useContentFilterStore();
  const [mutation, state] = useMutation<
    Pick<NexoyaMutation, 'updatePortfolioContentTargetBoundaries'>,
    NexoyaMutationUpdatePortfolioContentTargetBoundariesArgs & { contents?: number[] }
  >(UPDATE_PORTFOLIO_CONTENT_TARGET_BOUNDARIES, {
    notifyOnNetworkStatusChange: true,
    awaitRefetchQueries: true,
    // The limits dialog reports per-section failures itself; keep the generic errorLink toast quiet.
    context: { skipGlobalErrorToast: true },
    variables: {
      teamId,
      portfolioId,
      contents,
      newTarget,
    },
    onCompleted: () => track(EVENT.PORTFOLIO_CONTENT_TARGET_BOUNDARIES_UPDATE),
    refetchQueries: [
      {
        query: PORTFOLIO_PARENT_CONTENTS_QUERY,
        variables: buildPortfolioContentFilterVariables(teamId, portfolioId, filterStore),
      },
    ],
  });
  return [mutation, state];
}

export { UPDATE_PORTFOLIO_CONTENT_TARGET_BOUNDARIES, useUpdatePortfolioContentTargetBoundaries };
